import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Search,
  GraduationCap,
  TrendingUp,
  Award,
  Calendar,
  Mail,
  ChevronRight,
  BarChart3,
  Target,
  Plus,
  ClipboardCheck,
  Sparkles,
  ArrowRight,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { JobsiteReports } from '../components/JobsiteReports';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StudentsPage = () => {
  const { user } = useAuth();
  const [pageTab, setPageTab] = useState('overview');
  const [addInternOpen, setAddInternOpen] = useState(false);
  const [internForm, setInternForm] = useState({ name: '', student_id: '', batch_id: '', email: '', newClassName: '' });
  const [savingIntern, setSavingIntern] = useState(false);
  const [students, setStudents] = useState([]);
  const [batches, setBatches] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedBatch, setSelectedBatch] = useState('all');
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [learningPath, setLearningPath] = useState(null);
  const [pathLoading, setPathLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  // Assessment Entry Dialog
  const [assessmentOpen, setAssessmentOpen] = useState(false);
  const [assessmentData, setAssessmentData] = useState({
    student_id: '',
    course_name: '',
    assessment_name: '',
    score: '',
    max_score: '100',
    feedback: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [studentsRes, batchesRes, assessmentsRes] = await Promise.all([
        axios.get(`${API}/students`),
        axios.get(`${API}/batches`),
        axios.get(`${API}/assessments`),
      ]);
      setStudents(studentsRes.data);
      setBatches(batchesRes.data);
      setAssessments(assessmentsRes.data);
    } catch (error) {
      console.error('Error fetching student data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const saveIntern = async () => {
    if (!internForm.name.trim()) { toast.error('Intern name is required'); return; }
    const wantsNewClass = internForm.batch_id === '__new__';
    if (!internForm.batch_id || (wantsNewClass && !internForm.newClassName.trim())) {
      toast.error('Pick a class or name a new one');
      return;
    }
    setSavingIntern(true);
    try {
      let batchId = internForm.batch_id;
      if (wantsNewClass) {
        const res = await axios.post(`${API}/batches`, {
          name: internForm.newClassName.trim(),
          course_name: 'Project LIFE',
          start_date: new Date().toISOString(),
        });
        batchId = res.data.id;
      }
      await axios.post(`${API}/students`, {
        name: internForm.name.trim(),
        student_id: internForm.student_id.trim() || `PL-${Date.now().toString().slice(-6)}`,
        batch_id: batchId,
        email: internForm.email.trim() || null,
      });
      toast.success(`${internForm.name.trim()} added`);
      setAddInternOpen(false);
      setInternForm({ name: '', student_id: '', batch_id: '', email: '', newClassName: '' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not add the intern');
    }
    setSavingIntern(false);
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getStudentAssessments = (studentId) => {
    return assessments.filter((a) => a.student_id === studentId);
  };

  const calculateAverageScore = (studentId) => {
    const studentAssessments = getStudentAssessments(studentId);
    if (studentAssessments.length === 0) return 0;
    const total = studentAssessments.reduce(
      (acc, a) => acc + (a.score / a.max_score) * 100,
      0
    );
    return Math.round(total / studentAssessments.length);
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-[#3d7a22]';
    if (score >= 60) return 'text-[#b34e00]';
    return 'text-[#b34e00]';
  };

  const filteredStudents = students.filter((student) => {
    const matchesSearch = student.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      student.student_id.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesBatch = selectedBatch === 'all' || student.batch_id === selectedBatch;
    return matchesSearch && matchesBatch;
  });

  const getBatchName = (batchId) => {
    const batch = batches.find((b) => b.id === batchId);
    return batch?.name || 'Unknown Batch';
  };

  // Open assessment dialog for a specific student
  const openAssessmentDialog = (student = null) => {
    setAssessmentData({
      student_id: student?.id || '',
      course_name: 'Work Skills Development',
      assessment_name: '',
      score: '',
      max_score: '100',
      feedback: ''
    });
    setAssessmentOpen(true);
  };

  // Submit assessment
  const handleSubmitAssessment = async () => {
    if (!assessmentData.student_id) {
      toast.error('Please select a student');
      return;
    }
    if (!assessmentData.assessment_name.trim()) {
      toast.error('Please enter an assessment name');
      return;
    }
    if (!assessmentData.score || isNaN(parseFloat(assessmentData.score))) {
      toast.error('Please enter a valid score');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/assessments`, {
        student_id: assessmentData.student_id,
        course_name: assessmentData.course_name,
        assessment_name: assessmentData.assessment_name,
        score: parseFloat(assessmentData.score),
        max_score: parseFloat(assessmentData.max_score) || 100,
        feedback: assessmentData.feedback
      }, {
        params: { assessed_by: user?.id || 'instructor' }
      });
      
      toast.success('Assessment recorded successfully!');
      setAssessmentOpen(false);
      fetchData();
      
      // If viewing a student, refresh to show new assessment
      if (selectedStudent?.id === assessmentData.student_id) {
        const updatedStudent = students.find(s => s.id === assessmentData.student_id);
        if (updatedStudent) {
          setSelectedStudent(updatedStudent);
        }
      }
    } catch (error) {
      toast.error('Failed to save assessment');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Predefined assessment types
  const assessmentTypes = [
    'Safety Signs Recognition',
    'Hazard Identification',
    'Emergency Response',
    'Communication Skills',
    'Time Management',
    'Workplace Safety',
    'Self-Assessment',
    'Goal Setting',
    'Decision Making',
    'Interview Skills',
    'Resume Writing',
    'Financial Literacy',
    'Technology Skills',
    'Social Skills',
    'Custom Assessment'
  ];

  // Course names from units
  const courseNames = [
    'Work Skills Development',
    'Life Skills Training',
    'Unit 1 - Safety',
    'Unit 2 - Self-Determination',
    'Unit 3 - Independence',
    'Unit 4 - Social & Communication Skills',
    'Unit 5 - Financial Skills',
    'Unit 6 - Technology Skills',
    'Unit 7 - Physical & Emotional Well-Being',
    'Unit 8 - Microenterprise Fundamentals'
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" data-testid="students-loading">
        <div className="w-8 h-8 border-4 border-[#34657f] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="students-page">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope'] text-[#1A1A1A]">
            Student Progress
          </h1>
          <p className="text-[#5C5C5C] mt-1">
            Track and manage student assessments and progress
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            className="border-[#34657f] text-[#34657f] hover:bg-[#34657f] hover:text-white"
            onClick={() => setAddInternOpen(true)}
            data-testid="add-intern-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Intern
          </Button>
          <Button
            className="bg-[#3d7a22] hover:bg-[#5aab3d]"
            onClick={() => openAssessmentDialog()}
            data-testid="add-assessment-btn"
          >
            <ClipboardCheck className="w-4 h-4 mr-2" />
            Record Assessment
          </Button>
        </div>
      </div>

      {/* Section switcher */}
      <div className="inline-flex rounded-lg border border-slate-300 bg-white p-1 gap-1" role="tablist" aria-label="Intern Progress sections">
        {[{ id: 'overview', label: 'Intern Overview' }, { id: 'reports', label: 'Daily Jobsite Reports' }].map((t) => (
          <button key={t.id} type="button" role="tab" aria-selected={pageTab === t.id}
            onClick={() => setPageTab(t.id)}
            className={`px-4 py-1.5 rounded-md text-sm font-semibold transition-colors ${
              pageTab === t.id ? 'bg-[#34657f] text-white' : 'text-[#5C5C5C] hover:text-[#1A1A1A]'
            }`}
            data-testid={`students-tab-${t.id}`}>
            {t.label}
          </button>
        ))}
      </div>

      {pageTab === 'reports' && <JobsiteReports students={students} batches={batches} />}

      {pageTab === 'overview' && (<>
      {/* Stats Overview */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card className="dashboard-card" data-testid="total-students-stat">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#5C5C5C]">Total Students</p>
                <p className="text-3xl font-bold font-['Manrope'] text-[#1A1A1A]">
                  {students.length}
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-[#34657f] flex items-center justify-center">
                <GraduationCap className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="dashboard-card" data-testid="active-batches-stat">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#5C5C5C]">Active Batches</p>
                <p className="text-3xl font-bold font-['Manrope'] text-[#1A1A1A]">
                  {batches.filter((b) => b.is_active).length}
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-[#3d7a22] flex items-center justify-center">
                <Target className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="dashboard-card" data-testid="assessments-stat">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#5C5C5C]">Total Assessments</p>
                <p className="text-3xl font-bold font-['Manrope'] text-[#1A1A1A]">
                  {assessments.length}
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-[#b34e00] flex items-center justify-center">
                <BarChart3 className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="dashboard-card" data-testid="avg-score-stat">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#5C5C5C]">Avg. Score</p>
                <p className="text-3xl font-bold font-['Manrope'] text-[#3d7a22]">
                  {assessments.length > 0 
                    ? Math.round(assessments.reduce((acc, a) => acc + (a.score / a.max_score) * 100, 0) / assessments.length)
                    : 0}%
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-[#b34e00] flex items-center justify-center">
                <Award className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filters */}
      <Card className="dashboard-card" data-testid="student-filters">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Search students by name or ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-12 border-slate-300 focus:border-[#34657f]"
                data-testid="student-search-input"
              />
            </div>
            <Select value={selectedBatch} onValueChange={setSelectedBatch}>
              <SelectTrigger className="w-full sm:w-64 h-12" aria-label="Filter by batch" data-testid="batch-filter">
                <SelectValue placeholder="Filter by batch" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Batches</SelectItem>
                {batches.map((batch) => (
                  <SelectItem key={batch.id} value={batch.id}>
                    {batch.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Students List */}
      <div className="space-y-4">
        {filteredStudents.length > 0 ? (
          filteredStudents.map((student) => {
            const avgScore = calculateAverageScore(student.id);
            const studentAssessments = getStudentAssessments(student.id);
            
            return (
              <Card
                key={student.id}
                className="dashboard-card interactive-card cursor-pointer"
                onClick={() => setSelectedStudent(student)}
                data-testid={`student-card-${student.id}`}
              >
                <CardContent className="p-6">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                    {/* Student Info */}
                    <div className="flex items-center gap-4 flex-1">
                      <Avatar className="h-14 w-14">
                        <AvatarImage src={student.avatar_url} alt={student.name} />
                        <AvatarFallback className="bg-[#34657f] text-white font-semibold text-lg">
                          {getInitials(student.name)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-lg text-[#1A1A1A]">
                          {student.name}
                        </h3>
                        <div className="flex items-center gap-3 flex-wrap mt-1">
                          <span className="badge-blue">{student.student_id}</span>
                          <span className="text-sm text-[#5C5C5C]">
                            {getBatchName(student.batch_id)}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Progress Stats */}
                    <div className="flex items-center gap-6 sm:gap-8">
                      <div className="text-center">
                        <p className="text-sm text-[#5C5C5C]">Assessments</p>
                        <p className="text-xl font-bold text-[#1A1A1A]">
                          {studentAssessments.length}
                        </p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm text-[#5C5C5C]">Avg. Score</p>
                        <p className={`text-xl font-bold ${getScoreColor(avgScore)}`}>
                          {avgScore}%
                        </p>
                      </div>
                      <div className="w-24 hidden sm:block">
                        <Progress 
                          value={avgScore} 
                          className="h-2"
                          aria-label={`Average score ${avgScore}%`}
                        />
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="hidden sm:flex"
                        onClick={(e) => {
                          e.stopPropagation();
                          openAssessmentDialog(student);
                        }}
                        data-testid={`quick-assess-${student.id}`}
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Assess
                      </Button>
                      <ChevronRight className="w-5 h-5 text-slate-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        ) : (
          <Card className="dashboard-card">
            <CardContent className="py-12 text-center">
              <GraduationCap className="w-16 h-16 mx-auto mb-4 text-slate-300" />
              <h3 className="text-lg font-semibold text-[#1A1A1A] mb-2">
                No students found
              </h3>
              <p className="text-[#5C5C5C]">
                {searchQuery
                  ? 'Try adjusting your search query'
                  : 'Students will appear here when enrolled'}
              </p>
            </CardContent>
          </Card>
        )}
      </div>
      </>)}

      {/* Add Intern Dialog */}
      <Dialog open={addInternOpen} onOpenChange={(o) => { setAddInternOpen(o); if (!o) setInternForm({ name: '', student_id: '', batch_id: '', email: '', newClassName: '' }); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add an intern</DialogTitle>
            <DialogDescription>New interns become available in assessments and daily jobsite reports right away.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="intern-name">Full name *</Label>
              <Input id="intern-name" placeholder="e.g. Jordan Smith" value={internForm.name}
                onChange={(e) => setInternForm((p) => ({ ...p, name: e.target.value }))} data-testid="intern-name-input" />
            </div>
            <div>
              <Label htmlFor="intern-class">Class *</Label>
              <Select value={internForm.batch_id} onValueChange={(v) => setInternForm((p) => ({ ...p, batch_id: v }))}>
                <SelectTrigger id="intern-class" data-testid="intern-class-select"><SelectValue placeholder="Choose a class…" /></SelectTrigger>
                <SelectContent>
                  {batches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                  <SelectItem value="__new__">+ Create a new class…</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {internForm.batch_id === '__new__' && (
              <div>
                <Label htmlFor="intern-new-class">New class name *</Label>
                <Input id="intern-new-class" placeholder="e.g. Fall 2026" value={internForm.newClassName}
                  onChange={(e) => setInternForm((p) => ({ ...p, newClassName: e.target.value }))} data-testid="intern-new-class-input" />
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="intern-id">Intern ID (optional)</Label>
                <Input id="intern-id" placeholder="Auto-generated if blank" value={internForm.student_id}
                  onChange={(e) => setInternForm((p) => ({ ...p, student_id: e.target.value }))} data-testid="intern-id-input" />
              </div>
              <div>
                <Label htmlFor="intern-email">Email (optional)</Label>
                <Input id="intern-email" type="email" placeholder="name@school.org" value={internForm.email}
                  onChange={(e) => setInternForm((p) => ({ ...p, email: e.target.value }))} data-testid="intern-email-input" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddInternOpen(false)}>Cancel</Button>
            <Button className="bg-[#3d7a22] hover:bg-[#5aab3d]" onClick={saveIntern} disabled={savingIntern} data-testid="intern-save-btn">
              {savingIntern ? 'Adding…' : 'Add intern'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Student Detail Modal */}
      <Dialog open={!!selectedStudent} onOpenChange={(open) => {
        if (!open) {
          setSelectedStudent(null);
          setLearningPath(null);
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedStudent && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-4">
                  <Avatar className="h-16 w-16">
                    <AvatarImage src={selectedStudent.avatar_url} alt={selectedStudent.name} />
                    <AvatarFallback className="bg-[#34657f] text-white font-semibold text-xl">
                      {getInitials(selectedStudent.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <DialogTitle className="text-2xl font-['Manrope']">
                      {selectedStudent.name}
                    </DialogTitle>
                    <div className="flex items-center gap-3 mt-1">
                      <span className="badge-blue">{selectedStudent.student_id}</span>
                      <Badge className={selectedStudent.is_active ? 'badge-green' : 'badge-orange'}>
                        {selectedStudent.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                  </div>
                  <Button
                    className="bg-[#3d7a22] hover:bg-[#5aab3d]"
                    onClick={() => openAssessmentDialog(selectedStudent)}
                    data-testid="modal-add-assessment-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Assessment
                  </Button>
                </div>
              </DialogHeader>

              <Tabs defaultValue="overview" className="mt-6" onValueChange={async (val) => {
                if (val === 'next-up' && selectedStudent && !learningPath) {
                  setPathLoading(true);
                  try {
                    const res = await axios.get(`${API}/students/${selectedStudent.id}/learning-path`);
                    setLearningPath(res.data);
                  } catch (e) {
                    console.error('learning path err', e);
                  } finally {
                    setPathLoading(false);
                  }
                }
              }}>
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="overview" data-testid="overview-tab">Overview</TabsTrigger>
                  <TabsTrigger value="next-up" data-testid="next-up-tab">
                    <Sparkles className="w-4 h-4 mr-1.5 text-[#b34e00]" />
                    Next Up
                  </TabsTrigger>
                  <TabsTrigger value="assessments" data-testid="assessments-tab">Assessments</TabsTrigger>
                </TabsList>

                <TabsContent value="overview" className="space-y-6 mt-6">
                  {/* Student Details */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 rounded-xl bg-[#F8F9FA]">
                      <div className="flex items-center gap-2 mb-2">
                        <Mail className="w-4 h-4 text-[#34657f]" />
                        <span className="text-sm text-[#5C5C5C]">Email</span>
                      </div>
                      <p className="font-medium text-[#1A1A1A]">
                        {selectedStudent.email || 'Not provided'}
                      </p>
                    </div>
                    <div className="p-4 rounded-xl bg-[#F8F9FA]">
                      <div className="flex items-center gap-2 mb-2">
                        <Calendar className="w-4 h-4 text-[#34657f]" />
                        <span className="text-sm text-[#5C5C5C]">Enrolled</span>
                      </div>
                      <p className="font-medium text-[#1A1A1A]">
                        {new Date(selectedStudent.enrolled_date).toLocaleDateString()}
                      </p>
                    </div>
                  </div>

                  {/* Progress Summary */}
                  <Card className="border-slate-200">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg font-['Manrope'] flex items-center gap-2">
                        <TrendingUp className="w-5 h-5 text-[#3d7a22]" />
                        Progress Summary
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-[#5C5C5C]">Overall Progress</span>
                        <span className={`font-bold text-lg ${getScoreColor(calculateAverageScore(selectedStudent.id))}`}>
                          {calculateAverageScore(selectedStudent.id)}%
                        </span>
                      </div>
                      <Progress 
                        value={calculateAverageScore(selectedStudent.id)} 
                        className="h-3"
                        aria-label={`Overall progress ${calculateAverageScore(selectedStudent.id)}%`}
                      />
                      <div className="mt-4 pt-4 border-t border-slate-200">
                        <div className="grid grid-cols-3 gap-4 text-center">
                          <div>
                            <p className="text-2xl font-bold text-[#34657f]">
                              {getStudentAssessments(selectedStudent.id).length}
                            </p>
                            <p className="text-sm text-[#5C5C5C]">Assessments</p>
                          </div>
                          <div>
                            <p className="text-2xl font-bold text-[#3d7a22]">
                              {getStudentAssessments(selectedStudent.id).filter(a => (a.score / a.max_score) >= 0.8).length}
                            </p>
                            <p className="text-sm text-[#5C5C5C]">Passed</p>
                          </div>
                          <div>
                            <p className="text-2xl font-bold text-[#b34e00]">
                              {getStudentAssessments(selectedStudent.id).filter(a => (a.score / a.max_score) < 0.6).length}
                            </p>
                            <p className="text-sm text-[#5C5C5C]">Needs Work</p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="next-up" className="mt-6 space-y-4" data-testid="next-up-content">
                  {pathLoading ? (
                    <div className="py-12 text-center text-[#5C5C5C]">Calculating learning path…</div>
                  ) : !learningPath ? (
                    <div className="py-12 text-center text-[#5C5C5C]">Open this tab to compute a personalized path.</div>
                  ) : learningPath.suggestions.length === 0 ? (
                    <div className="py-12 text-center">
                      <Sparkles className="w-10 h-10 mx-auto text-[#3d7a22] mb-3" />
                      <p className="font-['Manrope'] text-lg text-[#1A1A1A]">All clear!</p>
                      <p className="text-sm text-[#5C5C5C]">This student has assessments across every unit with passing scores.</p>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-2 text-sm text-[#5C5C5C]">
                        <Sparkles className="w-4 h-4 text-[#b34e00]" />
                        <span>Based on {learningPath.total_assessments} assessment{learningPath.total_assessments === 1 ? '' : 's'} and {learningPath.completed_units.length} unit{learningPath.completed_units.length === 1 ? '' : 's'} touched.</span>
                      </div>
                      {learningPath.suggestions.map((s, idx) => {
                        const priorityStyles = {
                          high: { bg: 'bg-orange-50', border: 'border-orange-200', dot: 'bg-[#b34e00]', label: 'High priority' },
                          medium: { bg: 'bg-amber-50', border: 'border-amber-200', dot: 'bg-amber-500', label: 'Needs review' },
                          low: { bg: 'bg-blue-50', border: 'border-blue-200', dot: 'bg-[#1f7fa6]', label: 'Recommended' },
                        }[s.priority] || { bg: 'bg-slate-50', border: 'border-slate-200', dot: 'bg-slate-400', label: 'Suggested' };
                        return (
                          <div key={`${s.unit_number}-${idx}`} className={`rounded-xl border ${priorityStyles.border} ${priorityStyles.bg} p-4`} data-testid={`learning-path-suggestion-${idx}`}>
                            <div className="flex items-start gap-3">
                              <div className={`mt-1.5 w-2.5 h-2.5 rounded-full ${priorityStyles.dot} shrink-0`} />
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-xs uppercase tracking-wide font-semibold text-[#5C5C5C]">{priorityStyles.label}</span>
                                  <span className="text-xs text-[#5C5C5C]">·</span>
                                  <span className="text-xs text-[#5C5C5C]">Unit {s.unit_number}</span>
                                </div>
                                <h4 className="font-['Manrope'] text-lg font-semibold text-[#1A1A1A]">
                                  Unit {s.unit_number} — {s.unit_name}
                                </h4>
                                <p className="text-sm text-[#5C5C5C] mt-1">{s.reason}</p>
                                <div className="mt-2 flex flex-wrap gap-1.5">
                                  {s.domains.map((d) => (
                                    <Badge key={d} variant="secondary" className="text-xs">{d}</Badge>
                                  ))}
                                </div>
                              </div>
                              <ArrowRight className="w-4 h-4 text-[#34657f] mt-1.5 shrink-0" />
                            </div>
                          </div>
                        );
                      })}
                    </>
                  )}
                </TabsContent>

                <TabsContent value="assessments" className="mt-6">
                  <div className="space-y-4">
                    {getStudentAssessments(selectedStudent.id).length > 0 ? (
                      getStudentAssessments(selectedStudent.id).map((assessment) => {
                        const scorePercent = Math.round((assessment.score / assessment.max_score) * 100);
                        return (
                          <div
                            key={assessment.id}
                            className="p-4 rounded-xl border border-slate-200 hover:border-[#34657f]/30 transition-colors"
                            data-testid={`assessment-${assessment.id}`}
                          >
                            <div className="flex items-start justify-between mb-3">
                              <div>
                                <h4 className="font-semibold text-[#1A1A1A]">
                                  {assessment.assessment_name}
                                </h4>
                                <p className="text-sm text-[#5C5C5C]">
                                  {assessment.course_name}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className={`text-xl font-bold ${getScoreColor(scorePercent)}`}>
                                  {assessment.score}/{assessment.max_score}
                                </p>
                                <p className="text-sm text-[#5C5C5C]">
                                  {scorePercent}%
                                </p>
                              </div>
                            </div>
                            {assessment.feedback && (
                              <div className="p-3 rounded-lg bg-[#F8F9FA]">
                                <p className="text-sm text-[#5C5C5C]">
                                  <span className="font-medium">Feedback:</span> {assessment.feedback}
                                </p>
                              </div>
                            )}
                            <p className="text-xs text-[#5C5C5C] mt-2">
                              Assessed: {new Date(assessment.assessed_at).toLocaleDateString()}
                            </p>
                          </div>
                        );
                      })
                    ) : (
                      <div className="text-center py-8 text-[#5C5C5C]">
                        <Award className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p>No assessments recorded yet</p>
                        <Button
                          className="mt-4 bg-[#3d7a22]"
                          onClick={() => openAssessmentDialog(selectedStudent)}
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Add First Assessment
                        </Button>
                      </div>
                    )}
                  </div>
                </TabsContent>
              </Tabs>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Assessment Entry Dialog */}
      <Dialog open={assessmentOpen} onOpenChange={setAssessmentOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-['Manrope'] text-xl flex items-center gap-2">
              <ClipboardCheck className="w-5 h-5 text-[#3d7a22]" />
              Record Assessment
            </DialogTitle>
            <DialogDescription>
              Enter assessment details for a student. All fields marked with * are required.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 pt-4">
            {/* Student Selection */}
            <div className="space-y-2">
              <Label>Student *</Label>
              <Select 
                value={assessmentData.student_id} 
                onValueChange={(v) => setAssessmentData(prev => ({ ...prev, student_id: v }))}
              >
                <SelectTrigger aria-label="Select student" data-testid="assessment-student-select">
                  <SelectValue placeholder="Select a student" />
                </SelectTrigger>
                <SelectContent>
                  {students.map((student) => (
                    <SelectItem key={student.id} value={student.id}>
                      {student.name} ({student.student_id})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Course Selection */}
            <div className="space-y-2">
              <Label>Course / Unit *</Label>
              <Select 
                value={assessmentData.course_name} 
                onValueChange={(v) => setAssessmentData(prev => ({ ...prev, course_name: v }))}
              >
                <SelectTrigger aria-label="Select course or unit" data-testid="assessment-course-select">
                  <SelectValue placeholder="Select course" />
                </SelectTrigger>
                <SelectContent>
                  {courseNames.map((course) => (
                    <SelectItem key={course} value={course}>
                      {course}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Assessment Type */}
            <div className="space-y-2">
              <Label>Assessment Type *</Label>
              <Select 
                value={assessmentData.assessment_name} 
                onValueChange={(v) => setAssessmentData(prev => ({ ...prev, assessment_name: v }))}
              >
                <SelectTrigger aria-label="Select assessment type" data-testid="assessment-type-select">
                  <SelectValue placeholder="Select assessment type" />
                </SelectTrigger>
                <SelectContent>
                  {assessmentTypes.map((type) => (
                    <SelectItem key={type} value={type}>
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {assessmentData.assessment_name === 'Custom Assessment' && (
                <Input
                  placeholder="Enter custom assessment name"
                  className="mt-2"
                  onChange={(e) => setAssessmentData(prev => ({ 
                    ...prev, 
                    assessment_name: e.target.value || 'Custom Assessment' 
                  }))}
                />
              )}
            </div>
            
            {/* Score */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="score">Score *</Label>
                <Input
                  id="score"
                  type="number"
                  min="0"
                  max={assessmentData.max_score}
                  value={assessmentData.score}
                  onChange={(e) => setAssessmentData(prev => ({ ...prev, score: e.target.value }))}
                  placeholder="e.g., 85"
                  data-testid="assessment-score-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="max_score">Max Score</Label>
                <Input
                  id="max_score"
                  type="number"
                  min="1"
                  value={assessmentData.max_score}
                  onChange={(e) => setAssessmentData(prev => ({ ...prev, max_score: e.target.value }))}
                  placeholder="100"
                />
              </div>
            </div>
            
            {/* Score Preview */}
            {assessmentData.score && assessmentData.max_score && (
              <div className="p-3 rounded-lg bg-[#F8F9FA] flex items-center justify-between">
                <span className="text-sm text-[#5C5C5C]">Score Preview</span>
                <span className={`text-lg font-bold ${getScoreColor(Math.round((parseFloat(assessmentData.score) / parseFloat(assessmentData.max_score)) * 100))}`}>
                  {Math.round((parseFloat(assessmentData.score) / parseFloat(assessmentData.max_score)) * 100)}%
                </span>
              </div>
            )}
            
            {/* Feedback */}
            <div className="space-y-2">
              <Label htmlFor="feedback">Feedback</Label>
              <Textarea
                id="feedback"
                value={assessmentData.feedback}
                onChange={(e) => setAssessmentData(prev => ({ ...prev, feedback: e.target.value }))}
                placeholder="Add feedback or notes about this assessment..."
                rows={3}
                data-testid="assessment-feedback-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssessmentOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmitAssessment}
              disabled={isSubmitting}
              className="bg-[#3d7a22] hover:bg-[#5aab3d]"
              data-testid="submit-assessment-btn"
            >
              {isSubmitting ? 'Saving...' : 'Save Assessment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StudentsPage;
